import { z } from 'zod';
import { amountSchema, dateOnlySchema } from './common';
/**
 * The reporting surface: the money dashboard, the breakdowns and the target it
 * is measured against.
 *
 * Separate from the existing `dashboard` module deliberately. The home screen
 * answers "what do I do now" and must be fast on every login; a report answers
 * "how did we do" and is opened occasionally over a wide range. One service
 * optimised for both is optimised for neither.
 */
/**
 * Which definition of income the report is built on.
 *
 * `accrual` - an invoice raised is revenue earned. What GSTR-1 is built from,
 *             what the CA reads, and the only like-for-like comparison with the
 *             expense side, where an expense row IS both the cost and the cash
 *             movement on the same date.
 * `cash`    - a payment allocated is revenue realised. The mental model most
 *             small business owners actually hold, and the assessable figure
 *             under presumptive taxation.
 *
 * The toggle moves EVERY figure on the response together - income, profit,
 * cumulative profit and the goal variance - so no screen is ever half one basis
 * and half the other.
 *
 * Note what the basis does NOT change: money received with no document behind
 * it. A deposit is cash under either heading, so it is counted the same way in
 * both, and neither basis can pair it with an invoice that does not exist.
 */
export const REPORT_BASES = ['accrual', 'cash'];
export const reportRangeQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    basis: z.enum(REPORT_BASES).default('accrual'),
})
    .strict()
    .refine((v) => v.from <= v.to, {
    path: ['to'],
    message: 'The end of the range comes before its start',
});
export const REPORT_SIDES = ['income', 'expense', 'both'];
export const reportBreakdownQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    basis: z.enum(REPORT_BASES).default('accrual'),
    side: z.enum(REPORT_SIDES).default('both'),
})
    .strict()
    .refine((v) => v.from <= v.to, {
    path: ['to'],
    message: 'The end of the range comes before its start',
});
/**
 * The month-by-month series.
 *
 * Anchored on a financial year rather than on an arbitrary range, because
 * cumulative profit has to reset somewhere and "the first day of the selected
 * financial year" is the only defensible place. All-time would include the
 * period before the books moved into Fint, which is neither zero nor known -
 * `parties.openingBalance` exists precisely because businesses arrive mid-life,
 * and an all-time cumulative figure would be confidently wrong.
 *
 * `fyStart` is a date rather than a year number so the client never has to know
 * the org's `fiscalYearStartMonth` to ask the question; the response echoes it
 * back so the axis can be labelled "since 1 Apr 2026" rather than inferred.
 */
export const reportOverviewQuerySchema = z
    .object({
    fyStart: dateOnlySchema.optional(),
    basis: z.enum(REPORT_BASES).default('accrual'),
})
    .strict();
export const TARGET_METRICS = ['income', 'profit'];
export const TARGET_PERIOD_TYPES = ['month', 'year'];
/**
 * Set the target for a run of periods in one call.
 *
 * This is what "set the same target for every month of this financial year"
 * sends: twelve entries, one request, one transaction, one audit event. An even
 * split of an annual figure is deliberately not offered - it is wrong for any
 * business with a season, which in India is most of them.
 */
export const setTargetsSchema = z
    .object({
    metric: z.enum(TARGET_METRICS).default('income'),
    periodType: z.enum(TARGET_PERIOD_TYPES).default('month'),
    entries: z
        .array(z
        .object({
        periodStart: dateOnlySchema,
        amount: amountSchema,
    })
        .strict())
        .min(1)
        .max(60),
})
    .strict()
    .superRefine((v, ctx) => {
    v.entries.forEach((entry, i) => {
        // A target dated the 15th would silently never match a month bucket, and
        // the variance column would read "no target set" for a month somebody
        // believes they set one on.
        if (v.periodType === 'month' && !entry.periodStart.endsWith('-01')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['entries', i, 'periodStart'],
                message: 'A monthly target is dated the first of the month it applies to.',
            });
        }
    });
});
export const listTargetsQuerySchema = z
    .object({
    metric: z.enum(TARGET_METRICS).optional(),
    periodType: z.enum(TARGET_PERIOD_TYPES).default('month'),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
})
    .strict();
/**
 * The first day of the financial year a date falls in.
 *
 * Shared by the API and the SPA so "since 1 Apr" is computed once. Pure string
 * arithmetic on `YYYY-MM-DD`: passing a date-only string to `new Date()` reads
 * it as midnight UTC and shifts the day for anyone west of Greenwich, and the
 * financial year is exactly the boundary where that produces the wrong year.
 */
export function fiscalYearStart(date, fiscalYearStartMonth) {
    const [y, m] = date.split('-').map((part) => Number(part));
    if (!Number.isFinite(y) || !Number.isFinite(m))
        return date;
    const year = m >= fiscalYearStartMonth ? y : y - 1;
    return `${String(year).padStart(4, '0')}-${String(fiscalYearStartMonth).padStart(2, '0')}-01`;
}
/** The twelve `YYYY-MM` buckets of a financial year starting on `fyStart`. */
export function fiscalYearMonths(fyStart) {
    const [y, m] = fyStart.split('-').map((part) => Number(part));
    const out = [];
    for (let i = 0; i < 12; i += 1) {
        const month = ((m - 1 + i) % 12) + 1;
        const year = y + Math.floor((m - 1 + i) / 12);
        out.push(`${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`);
    }
    return out;
}
/**
 * The day AFTER the last day of a financial year.
 *
 * Every range predicate in the reports is half-open - `date >= from AND date <
 * end` - because that is what keeps the range on the indexed column and needs
 * no knowledge of month lengths or leap years.
 */
export function fiscalYearEndExclusive(fyStart) {
    const [y, m] = fyStart.split('-').map((part) => Number(part));
    return `${String(y + 1).padStart(4, '0')}-${String(m).padStart(2, '0')}-01`;
}
/** The day after `date`. Turns an inclusive `to` into a half-open bound. */
export function dayAfter(date) {
    const [y, m, d] = date.split('-').map((part) => Number(part));
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    return next.toISOString().slice(0, 10);
}
// ── breakdowns by dimension, and month-by-month by dimension ──────────────
/**
 * The ways income can be split. `category` is the income category - one
 * bucket for most service businesses, which is why the others exist: an
 * invoice line already names its catalogue item and its HSN/SAC, and the
 * document names its customer, so those three splits cost nothing to keep.
 */
export const INCOME_DIMENSIONS = ['item', 'hsn', 'customer', 'category'];
/**
 * The ways spend can be split. Tags are deliberately NOT here: a record can
 * carry several, so a by-tag split overlaps and does not add up to the
 * total - `/reports/tags` says so in its own response and stays separate.
 */
export const EXPENSE_DIMENSIONS = ['category', 'vendor', 'project'];
/**
 * Which earlier window a figure is set against. `previous` is the window of
 * the same length ending the day before this one (last month, last quarter);
 * `year` is the same dates one year earlier (September against September,
 * April to September against April to September). Both, by default, because
 * a hover wants to say both.
 */
export const REPORT_COMPARISONS = ['none', 'previous', 'year', 'both'];
const oneSideSchema = z.enum(['income', 'expense']);
const dimensionOf = (side, by) => {
    const list = side === 'income' ? INCOME_DIMENSIONS : EXPENSE_DIMENSIONS;
    return list.includes(by) ? by : null;
};
const defaultDimension = (side) => (side === 'income' ? 'item' : 'category');
function refineDimension(v, ctx) {
    if (v.from > v.to) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'The end of the range comes before its start' });
    }
    const by = v.by ?? defaultDimension(v.side);
    if (!dimensionOf(v.side, by)) {
        const allowed = (v.side === 'income' ? INCOME_DIMENSIONS : EXPENSE_DIMENSIONS).join(', ');
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['by'],
            message: `${v.side === 'income' ? 'Income' : 'Spend'} can be split by ${allowed}, not "${by}"`,
        });
    }
}
/** A window with a start no later than its end, and the dimension checked against the side. */
export const reportDimensionQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    basis: z.enum(REPORT_BASES).default('accrual'),
    side: oneSideSchema,
    by: z.string().trim().min(1).max(20).optional(),
    compare: z.enum(REPORT_COMPARISONS).default('both'),
})
    .strict()
    .superRefine(refineDimension)
    .transform((v) => ({ ...v, by: (v.by ?? defaultDimension(v.side)) }));
/**
 * A report pack for a period: both sides, as a document or a workbook.
 *
 * No `side` and no `by`: a pack is the whole picture, and each side takes the
 * dimension that reads best for it (income by item, spend by category) rather
 * than making somebody choose twice before they can press a button.
 */
export const REPORT_EXPORT_FORMATS = ['pdf', 'xlsx'];
export const reportExportQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    basis: z.enum(REPORT_BASES).default('accrual'),
    format: z.enum(REPORT_EXPORT_FORMATS).default('pdf'),
})
    .strict()
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'The end of the range comes before its start' });
/** The month-by-month matrix: the same window and dimension, plus how many buckets to name before "Other". */
export const reportMonthsQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    basis: z.enum(REPORT_BASES).default('accrual'),
    side: oneSideSchema,
    by: z.string().trim().min(1).max(20).optional(),
    top: z.coerce.number().int().min(1).max(12).default(6),
})
    .strict()
    .superRefine(refineDimension)
    .transform((v) => ({ ...v, by: (v.by ?? defaultDimension(v.side)) }));
//# sourceMappingURL=reports.js.map